import { CheckCircle, ChevronRight, XCircle } from '@untitledui/icons';
import { useCallback, useMemo } from 'react';

import { useAgentFeed } from '@/lib/agent-feed/hooks/useAgentFeed';
import type {
  AgentFeedItem,
  DoneTodayExecutedItem,
  DoneTodayItem,
  DoneTodayProposalItem,
} from '@/lib/agent-feed/types';
import {
  Accordion,
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion/accordion';
import { BadgeWithDot } from '@/lib/base/badges/badges';
import { CollapsibleContent } from '@/lib/base/collapsible-content';
import { EmptyState } from '@/lib/base/empty-state/empty-state';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { QueryErrorState } from '@/lib/components';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';
import { formatRelativeTime } from '@/lib/utils/timeFormatting';
import { cx } from '@/utils/cx';

import { DetailPanelCard } from './shared/DetailPanelCard';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const TRIGGER_SOURCE_LABELS: Record<string, string> = {
  email_received: 'New email received',
  meeting_notes: 'Meeting notes created',
  research: 'Research completed',
  research_complete: 'Research completed',
  meeting_prep_ready: 'Meeting prep ready',
  email_opened: 'Email opened',
};

const LEFT_BORDER_COLOR: Record<string, string> = {
  evaluating: 'border-l-[var(--color-gray-300)]',
  preparing: 'border-l-[var(--color-warning-400)]',
};

// Collapsed preview height for a no-action reason — about 2 lines of text-xs.
const REASON_PREVIEW_MAX_HEIGHT_PX = 36;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatTriggerSources(sources: ReadonlyArray<string>): string {
  if (!sources || sources.length === 0) {
    return '';
  }
  return sources.map(key => TRIGGER_SOURCE_LABELS[key] ?? key).join(' · ');
}

// ---------------------------------------------------------------------------
// In-Progress card
// ---------------------------------------------------------------------------

function InProgressCard({
  item,
  orgId,
}: {
  item: AgentFeedItem;
  orgId: number | null;
}) {
  const subText = formatTriggerSources(item.triggerSources);
  const badgeLabel = item.status === 'evaluating' ? 'Evaluating' : 'Preparing';
  const badgeColor = item.status === 'evaluating' ? 'gray' : 'warning';

  return (
    <DetailPanelCard
      className={cx(
        'border-l-[3px]',
        LEFT_BORDER_COLOR[item.status] ?? 'border-l-[var(--color-gray-300)]'
      )}
    >
      <div className='px-4 py-3'>
        {/* Top row: badge + timestamp */}
        <div className='flex items-center gap-2 mb-1.5'>
          <BadgeWithDot size='sm' color={badgeColor} type='color'>
            {badgeLabel}
          </BadgeWithDot>
          <span className='text-xs text-[var(--color-text-tertiary)]'>
            {formatRelativeTime(item.createdAt)}
          </span>
        </div>

        {/* Entity — clickable EntityBadge that auto-fetches from CRM store */}
        <div className='flex items-center gap-1.5 flex-wrap'>
          {item.entityId && orgId ? (
            <EntityBadgeById
              entityId={item.entityId}
              orgId={orgId}
              variant='inline'
            />
          ) : (
            <span className='text-sm font-semibold text-[var(--color-text-primary)]'>
              {item.entityName}
            </span>
          )}
          {subText && (
            <span className='text-xs text-[var(--color-text-tertiary)]'>
              · {subText}
            </span>
          )}
        </div>
      </div>
    </DetailPanelCard>
  );
}

// ---------------------------------------------------------------------------
// Done Today — grouped by trigger entity, with subject entities nested
// ---------------------------------------------------------------------------

/** Group items by trigger entity ID (falls back to 'unknown' key). */
function groupByTrigger<T extends { triggerEntityId?: string }>(
  items: ReadonlyArray<T>
): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  for (const item of items) {
    const key = item.triggerEntityId ?? 'unknown';
    const group = groups.get(key);
    if (group) {
      group.push(item);
    } else {
      groups.set(key, [item]);
    }
  }
  return groups;
}

/** Render a trigger entity header — inline EntityBadge + trigger source + time. */
function TriggerHeader({
  item,
  orgId,
  completedAt,
}: {
  item: DoneTodayItem | DoneTodayProposalItem;
  orgId: number | null;
  completedAt: string;
}) {
  const subText = formatTriggerSources(item.triggerSources);
  return (
    <div className='flex items-center gap-1.5 py-1.5'>
      <div className='flex-1 min-w-0 flex items-center gap-1.5 flex-wrap'>
        {item.triggerEntityId && orgId ? (
          <EntityBadgeById
            entityId={item.triggerEntityId}
            orgId={orgId}
            variant='inline'
          />
        ) : (
          <span className='text-sm font-medium text-[var(--color-text-primary)]'>
            {item.triggerEntityName ?? 'Unknown trigger'}
          </span>
        )}
        {subText && (
          <span className='text-xs text-[var(--color-text-tertiary)]'>
            · {subText}
          </span>
        )}
      </div>
      <span className='shrink-0 text-xs text-[var(--color-text-tertiary)]'>
        {formatRelativeTime(completedAt)}
      </span>
    </div>
  );
}

/** Render a subject entity as an xs EntityBadge or plain text fallback. */
function SubjectLabel({
  subjectEntityId,
  subjectEntityName,
  orgId,
}: {
  subjectEntityId?: string;
  subjectEntityName?: string;
  orgId: number | null;
}) {
  if (subjectEntityId && orgId) {
    return (
      <EntityBadgeById entityId={subjectEntityId} orgId={orgId} variant='xs' />
    );
  }
  if (subjectEntityName) {
    return (
      <span className='text-xs text-[var(--color-text-secondary)]'>
        {subjectEntityName}
      </span>
    );
  }
  return null;
}

/** A subject entity row inside a trigger group (no-action items). */
function SubjectRow({
  item,
  orgId,
}: {
  item: DoneTodayItem;
  orgId: number | null;
}) {
  return (
    <li className='flex flex-col py-0.5 pl-5'>
      <div className='flex items-center gap-1.5'>
        <CheckCircle className='size-3 shrink-0 text-[var(--color-success-500)]' />
        <SubjectLabel
          subjectEntityId={item.subjectEntityId}
          subjectEntityName={item.subjectEntityName}
          orgId={orgId}
        />
      </div>
      {item.reason && (
        <div className='mt-0.5 pl-[18px] text-xs text-[var(--color-text-tertiary)]'>
          <CollapsibleContent maxCollapsedHeight={REASON_PREVIEW_MAX_HEIGHT_PX}>
            <MarkdownContent
              content={item.reason}
              size='compact'
              className='text-xs text-[var(--color-text-tertiary)] break-words max-w-none [&_p]:!my-0 [&_*]:text-inherit'
            />
          </CollapsibleContent>
        </div>
      )}
    </li>
  );
}

/** A proposal row inside a trigger group (proposals-created items). */
function ProposalSubjectRow({ item }: { item: DoneTodayProposalItem }) {
  const openSlideout = useOpenSlideoutOptional();
  const canOpen = Boolean(item.proposalId && openSlideout);

  const handleClick = useCallback(() => {
    if (item.proposalId && openSlideout) {
      openSlideout({
        kind: 'proposal',
        id: String(item.proposalId),
      });
    }
  }, [item.proposalId, openSlideout]);

  return (
    <li
      className={cx(
        'flex items-center gap-1.5 py-0.5 pl-5 rounded-md',
        canOpen &&
          'cursor-pointer hover:bg-[var(--color-bg-tertiary)] transition-colors duration-150'
      )}
      onClick={canOpen ? handleClick : undefined}
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
    >
      <CheckCircle className='size-3 shrink-0 text-[var(--color-success-500)]' />
      <span className='flex-1 min-w-0 text-xs text-[var(--color-text-primary)] truncate'>
        {item.title}
      </span>
      {canOpen && (
        <ChevronRight className='size-3 shrink-0 text-[var(--color-text-tertiary)]' />
      )}
    </li>
  );
}

/** Grouped no-action list: trigger headers with subject rows nested. */
function NoActionGroupedList({
  items,
  orgId,
}: {
  items: ReadonlyArray<DoneTodayItem>;
  orgId: number | null;
}) {
  const groups = useMemo(() => groupByTrigger(items), [items]);

  return (
    <ul className='flex flex-col divide-y divide-[var(--color-border-secondary)]'>
      {[...groups.entries()].map(([triggerKey, groupItems]) => (
        <li key={triggerKey} className='py-1'>
          <TriggerHeader
            item={groupItems[0]}
            orgId={orgId}
            completedAt={groupItems[0].completedAt}
          />
          <ul className='flex flex-col'>
            {groupItems.map(item => (
              <SubjectRow key={item.id} item={item} orgId={orgId} />
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/** Grouped proposals-created list: trigger headers with proposal rows nested. */
function ProposalsCreatedGroupedList({
  items,
  orgId,
}: {
  items: ReadonlyArray<DoneTodayProposalItem>;
  orgId: number | null;
}) {
  const groups = useMemo(() => groupByTrigger(items), [items]);

  return (
    <ul className='flex flex-col divide-y divide-[var(--color-border-secondary)]'>
      {[...groups.entries()].map(([triggerKey, groupItems]) => (
        <li key={triggerKey} className='py-1'>
          <TriggerHeader
            item={groupItems[0]}
            orgId={orgId}
            completedAt={groupItems[0].completedAt}
          />
          <ul className='flex flex-col'>
            {groupItems.map(item => (
              <ProposalSubjectRow key={item.id} item={item} />
            ))}
          </ul>
        </li>
      ))}
    </ul>
  );
}

/** Proposals executed — simple list (no trigger grouping, these are user actions). */
function DoneTodayExecutedRow({ item }: { item: DoneTodayExecutedItem }) {
  const openSlideout = useOpenSlideoutOptional();
  const canOpen = Boolean(item.id && openSlideout);

  const handleClick = useCallback(() => {
    if (item.id && openSlideout) {
      openSlideout({ kind: 'proposal', id: String(item.id) });
    }
  }, [item.id, openSlideout]);

  return (
    <li
      className={cx(
        'flex items-center gap-2.5 py-2 px-1 -mx-1 rounded-md',
        canOpen &&
          'cursor-pointer hover:bg-[var(--color-bg-tertiary)] transition-colors duration-150'
      )}
      onClick={canOpen ? handleClick : undefined}
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
    >
      <CheckCircle className='size-4 shrink-0 text-[var(--color-success-500)]' />
      <div className='flex-1 min-w-0'>
        <span className='text-sm font-medium text-[var(--color-text-primary)] truncate block'>
          {item.title}
        </span>
      </div>
      <span className='shrink-0 text-xs text-[var(--color-text-tertiary)]'>
        {formatRelativeTime(item.executedAt)}
      </span>
      {canOpen && (
        <ChevronRight className='size-3.5 shrink-0 text-[var(--color-text-tertiary)]' />
      )}
    </li>
  );
}

// ---------------------------------------------------------------------------
// Loading skeleton
// ---------------------------------------------------------------------------

function FeedSkeleton() {
  return (
    <div
      data-testid='agent-feed-loading'
      role='status'
      aria-busy='true'
      className='flex flex-col gap-3'
    >
      <Skeleton variant='text' width='40%' height={16} />
      <Skeleton variant='rectangular' height={88} />
      <Skeleton variant='rectangular' height={88} />
      <Skeleton variant='text' width='30%' height={16} className='mt-3' />
      <Skeleton variant='rectangular' height={40} />
      <Skeleton variant='rectangular' height={40} />
      <span className='sr-only'>Loading agent activity</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface AgentFeedContentProps {
  orgId: number | null;
}

export function AgentFeedContent({ orgId }: AgentFeedContentProps) {
  const { inProgress, doneToday, isLoading, counts, error } =
    useAgentFeed(orgId);

  const isEmpty = useMemo(
    () => !isLoading && inProgress.length === 0 && counts.doneToday === 0,
    [isLoading, inProgress.length, counts.doneToday]
  );

  if (isLoading) {
    return <FeedSkeleton />;
  }

  if (error) {
    console.error('[agent-feed] Failed to load agent activity', {
      orgId,
      error,
    });
    return (
      <div data-testid='agent-feed-error'>
        <QueryErrorState
          error={error}
          label='Unable to load agent activity'
          size='sm'
        />
      </div>
    );
  }

  if (isEmpty) {
    return (
      <div data-testid='agent-feed-empty'>
        <EmptyState
          variant='empty'
          title='No agent activity yet today'
          description="When agents start working, you'll see them here."
          size='sm'
        />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-8'>
      {/* In Progress */}
      {inProgress.length > 0 && (
        <section data-testid='agent-feed-in-progress' aria-label='In progress'>
          <h3 className='mb-3 text-xs font-semibold uppercase tracking-widest text-[var(--color-text-tertiary)]'>
            In progress
          </h3>
          <div className='flex flex-col gap-2.5'>
            {inProgress.map(item => (
              <InProgressCard key={item.id} item={item} orgId={orgId} />
            ))}
          </div>
        </section>
      )}

      {/* Done Today */}
      {doneToday !== null && counts.doneToday > 0 && (
        <section data-testid='agent-feed-done-today' aria-label='Done today'>
          <h3 className='mb-3 text-xs font-semibold uppercase tracking-widest text-[var(--color-text-tertiary)]'>
            Done today
          </h3>
          <Accordion allowsMultipleExpanded className='flex flex-col gap-3'>
            {doneToday.noAction.length > 0 && (
              <AccordionItem>
                <DetailPanelCard>
                  <AccordionTrigger className='flex w-full items-center justify-between px-4 py-3.5 text-left hover:bg-[var(--color-bg-tertiary)] transition-colors duration-150'>
                    <span className='flex items-center gap-2.5'>
                      <XCircle className='size-[18px] text-[var(--color-text-tertiary)]' />
                      <span className='text-sm font-semibold text-[var(--color-text-primary)]'>
                        Reviewed – no action needed
                      </span>
                      <span className='inline-flex items-center justify-center rounded-full bg-[var(--color-bg-tertiary)] min-w-[20px] h-5 px-1.5 text-xs font-medium text-[var(--color-text-tertiary)]'>
                        {doneToday.noAction.length}
                      </span>
                    </span>
                    <AccordionChevron />
                  </AccordionTrigger>
                  <AccordionPanel className='border-t border-border-secondary px-4 py-2'>
                    <NoActionGroupedList
                      items={doneToday.noAction}
                      orgId={orgId}
                    />
                  </AccordionPanel>
                </DetailPanelCard>
              </AccordionItem>
            )}
            {doneToday.proposalsCreated.length > 0 && (
              <AccordionItem>
                <DetailPanelCard>
                  <AccordionTrigger className='flex w-full items-center justify-between px-4 py-3.5 text-left hover:bg-[var(--color-bg-tertiary)] transition-colors duration-150'>
                    <span className='flex items-center gap-2.5'>
                      <CheckCircle className='size-[18px] text-[var(--color-success-500)]' />
                      <span className='text-sm font-semibold text-[var(--color-text-primary)]'>
                        Proposals created
                      </span>
                      <span className='inline-flex items-center justify-center rounded-full bg-[var(--color-bg-tertiary)] min-w-[20px] h-5 px-1.5 text-xs font-medium text-[var(--color-text-tertiary)]'>
                        {doneToday.proposalsCreated.length}
                      </span>
                    </span>
                    <AccordionChevron />
                  </AccordionTrigger>
                  <AccordionPanel className='border-t border-border-secondary px-4 py-2'>
                    <ProposalsCreatedGroupedList
                      items={doneToday.proposalsCreated}
                      orgId={orgId}
                    />
                  </AccordionPanel>
                </DetailPanelCard>
              </AccordionItem>
            )}
            {doneToday.proposalsExecuted.length > 0 && (
              <AccordionItem>
                <DetailPanelCard>
                  <AccordionTrigger className='flex w-full items-center justify-between px-4 py-3.5 text-left hover:bg-[var(--color-bg-tertiary)] transition-colors duration-150'>
                    <span className='flex items-center gap-2.5'>
                      <CheckCircle className='size-[18px] text-[var(--color-success-500)]' />
                      <span className='text-sm font-semibold text-[var(--color-text-primary)]'>
                        Proposals executed
                      </span>
                      <span className='inline-flex items-center justify-center rounded-full bg-[var(--color-bg-tertiary)] min-w-[20px] h-5 px-1.5 text-xs font-medium text-[var(--color-text-tertiary)]'>
                        {doneToday.proposalsExecuted.length}
                      </span>
                    </span>
                    <AccordionChevron />
                  </AccordionTrigger>
                  <AccordionPanel className='border-t border-border-secondary px-4 py-2'>
                    <ul className='flex flex-col'>
                      {doneToday.proposalsExecuted.map(item => (
                        <DoneTodayExecutedRow key={item.id} item={item} />
                      ))}
                    </ul>
                  </AccordionPanel>
                </DetailPanelCard>
              </AccordionItem>
            )}
          </Accordion>
        </section>
      )}
    </div>
  );
}
